<template>
  <div class="template-selector" @mouseleave="selectionListVisible = false">
    <div class="name text-truncate" @click="toggleListVisible">
      <span v-if="selected.id">{{ getTemplateDisplayLabel(selected) }}</span>
      <span v-else>{{ placeholder }}</span>
      <i v-show="!selectionListVisible" class="fa fa-caret-down"></i>
      <i v-show="selectionListVisible" class="fa fa-caret-up"></i>
    </div>

    <div class="templates-list" v-if="selectionListVisible" :class="{ 'has-pages': templates.length < total }">
      <LoadingMask :active="loading"/>

      <div class="items">
        <div class="item text-truncate"
          v-for="item in templates" :key="item.id"
          @click="select(item)"
          :title="getTemplateDisplayLabel(item)"
          :class="{ 'inactive': Template.isInactive(item) }"
        >
          {{ getTemplateDisplayLabel(item) }}
        </div>
      </div>
      <b-pagination
        align="center"
        :value="page"
        :per-page="paging.size"
        :total-rows="total"
        :limit="3"
        @change="goToPage">
      </b-pagination>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import templatesService from '@/services/TemplatesService'
import { Template, TemplateFilters, SortTypes } from '@/types/Templates'
import { PageRequest } from '@/types/Support'
import LoadingMask from '@/components/generic/LoadingMask.vue'

const PAGE_SIZE = 5

@Component({
  components: {
    LoadingMask
  }
})
export default class TemplateSelector extends Vue {
  Template = Template
  templates: Template[] = []
  page = 1
  total = 0
  paging = new PageRequest<TemplateFilters>(PAGE_SIZE)
  selected = new Template()
  selectionListVisible = false
  loading = false

  @Prop({ required: false })
  selectedId: number

  @Prop({ required: false })
  enabled: boolean

  @Prop({ required: false })
  pageSize: number

  @Prop({ required: false, default: 'Seleccione un template' }) // tracking, fillgrid
  placeholder: string

  @Prop({ required: false, default: () => ['enabled', 'label', 'version'] })
  sorts: Array<string>

  @Prop({ required: false, default: () => [SortTypes.DESC, SortTypes.ASC, SortTypes.DESC] })
  sortTypes: Array<SortTypes>

  mounted () {
    this.paging.size = this.pageSize || PAGE_SIZE
    this.paging.filters.enabled = this.enabled
    this.paging.filters.sort = this.sorts
    this.paging.filters.sortType = this.sortTypes
    this.loadTemplates()
  }

  @Watch('selectedId')
  watchSelectedId (value: number) {
    if (value && value !== this.selected.id) {
      let found = this.templates.find(it => it.id === value)
      if (found) {
        this.select(found)
      }
    }
  }

  loadTemplates () {
    this.loading = true
    templatesService.getTemplates(this.paging).then((pageResponse) => {
      setTimeout(() => {
        this.loading = false
        this.templates = pageResponse.content.map(it => Object.freeze(it))
        this.total = pageResponse.totalElements
        if (this.page === 1) {
          this.$emit('loaded', this.templates)
        }
      }, 120)
    })
  }

  toggleListVisible () {
    this.selectionListVisible = !this.selectionListVisible
  }

  select (template: Template) {
    this.selected = template
    this.$emit('select', template)
    this.selectionListVisible = false
  }

  getTemplateDisplayLabel (template: Template) {
    return `${template.label} [v${template.version}]`
  }

  goToPage (page: number) {
    this.page = page
    this.paging.page = page - 1
    this.loadTemplates()
  }
}
</script>

<style lang="scss" scoped>
.template-selector {
  position: relative;
  height: 45px;
  box-shadow: 0 0 3px #bbbbbb;
  border-radius: 4px;
  cursor: pointer;
  transition: all 200ms ease;

  &:hover {
    box-shadow: 0 0 6px #bbbbbb;
  }

  .name {
    position: relative;
    padding: 11px 25px 11px 11px;

    i {
      position: absolute;
      top: 14px;
      right: 8px;
    }
  }

  .templates-list {
    position: absolute;
    z-index: 9;
    top: 46px;
    bottom: 0;
    left: 0;
    right: 0;
    height: 275px;
    box-shadow: 0 2px 7px #b1b1b1;
    border-radius: 0 0 4px 4px;
    background: #fff;
    background: linear-gradient(0deg, #fff 0%, #f3f3f3 100%);

    .items {
      height: 100%;
      overflow-y: auto;
      border-radius: 4px;
      margin-bottom: 10px;

      .item {
        padding: 10px 15px;
        font-size: 0.85rem;
        cursor: pointer;
        &.inactive {
          color: #c0c0c0;
        }

        i {
          margin-right: 8px;
          font-size: 1rem;
          color: #a3a3a3;
        }
        &:hover {
          background-color: var(--element-hover-bg-color);
        }
        &.title {
          color: #a3a3a3;
        }
      }
    }

    .pagination {
      display: none;
    }

    &.has-pages {
      .items {
        height: calc(100% - 55px);
      }
      .pagination {
        display: flex;
      }
    }
  }
}
</style>
